import { Link } from 'react-router';
import { formatDateOnly, formatDateTime } from '../dates';

export type ComplianceCycleAttention = string | {
  id?: string;
  title?: string;
  details?: string;
  severity?: string;
  href?: string;
};

export type ComplianceCycleLink = {
  id?: string;
  kind?: string;
  label?: string;
  reference?: string;
  status?: string;
  href: string;
};

export type ComplianceCycleOccurrence = {
  id: string;
  occurrence_number?: string;
  scheduled_due_date?: string;
  occurrence_due_date?: string;
  completed_on?: string;
  next_due_date?: string;
  status?: string;
  lifecycle_status?: string;
  version?: number;
  stage?: string;
  autopilot_stage?: string;
  ready_for_completion?: boolean;
  needs_human?: boolean;
  next_intervention?: string | null;
  provider_name?: string | null;
  attendance_at?: string | null;
  attendance_status?: string | null;
  proposed_attendance_at?: string | null;
  confirmed_attendance_at?: string | null;
  attention?: ComplianceCycleAttention[];
  attention_items?: ComplianceCycleAttention[];
  links?: ComplianceCycleLink[];
  canonical_links?: ComplianceCycleLink[];
  action_id?: string | null;
  work_item_id?: string | null;
  provider_id?: string | null;
  calendar_event_id?: string | null;
  communication_thread_id?: string | null;
  provider_approval_request_id?: string | null;
  provider_approval_status?: string | null;
  provider_approval_summary?: string | null;
  provider_approval_context?: {
    recommendedContractorId?: string | null;
    requiredCapabilityCodes?: string[];
    candidates?: Array<{
      contractorId: string;
      organisationId?: string;
      providerName: string;
      reasons?: string[];
      eligibility?: Record<string, boolean>;
      deliverability?: Record<string, unknown>;
    }>;
  } | null;
  recommended_contractor_id?: string | null;
  approved_contractor_id?: string | null;
  work_instruction_id?: string | null;
  instruction_status?: string | null;
  instruction_communication_id?: string | null;
  appointment_calendar_event_id?: string | null;
  evidence?: Array<{ id: string; document_id?: string | null; document_title?: string; review_status?: string }>;
  findings?: Array<{
    id: string;
    finding_number?: string;
    title: string;
    status?: string;
    severity?: 'Low' | 'Medium' | 'High' | 'Critical';
    due_at?: string | null;
    updated_at?: string;
  }>;
  remedials?: Array<{ id: string; action_number?: string; work_number?: string; title: string; status?: string }>;
};

const stages = [
  { key: 'due', label: 'Due' },
  { key: 'work', label: 'Work prepared' },
  { key: 'provider', label: 'Provider approval' },
  { key: 'instructed', label: 'Instructed' },
  { key: 'appointment', label: 'Appointment' },
  { key: 'evidence', label: 'Evidence' },
  { key: 'findings', label: 'Findings' },
  { key: 'remedials', label: 'Remedials' },
  { key: 'verification', label: 'Verification' },
  { key: 'complete', label: 'Complete' },
  { key: 'next-due', label: 'Next due' },
] as const;

function normalise(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

function stageIndex(value: string | undefined): number {
  const stage = normalise(value);
  if (!stage) return 0;
  if (stage.includes('next due')) return 10;
  if (stage.includes('complete') || stage.includes('closed')) return 9;
  if (stage.includes('verification') || stage.includes('verify')) return 8;
  if (stage.includes('remedial')) return 7;
  if (stage.includes('finding') || stage.includes('defect')) return 6;
  if (stage.includes('evidence') || stage.includes('report') || stage.includes('certificate')) return 5;
  if (stage.includes('appointment') || stage.includes('attendance')) return 4;
  if (stage.includes('instruct')) return 3;
  if (stage.includes('provider') || stage.includes('procurement') || stage.includes('approval')) return 2;
  if (stage.includes('work')) return 1;
  return 0;
}

function attentionText(item: ComplianceCycleAttention): { title: string; details?: string; severity?: string; href?: string } {
  if (typeof item === 'string') return { title: item };
  return {
    title: item.title ?? item.details ?? 'Review required',
    ...(item.title && item.details ? { details: item.details } : {}),
    ...(item.severity ? { severity: item.severity } : {}),
    ...(item.href ? { href: item.href } : {}),
  };
}

function canonicalLinks(occurrence: ComplianceCycleOccurrence): ComplianceCycleLink[] {
  const links = [...(occurrence.links ?? occurrence.canonical_links ?? [])];
  if (occurrence.work_item_id) links.push({ kind: 'Work', label: 'Open inspection work', href: `/work/cases/${occurrence.work_item_id}` });
  const providerId = occurrence.provider_id ?? occurrence.approved_contractor_id;
  const calendarEventId = occurrence.calendar_event_id ?? occurrence.appointment_calendar_event_id;
  if (providerId) links.push({ kind: 'Provider', label: occurrence.provider_name ?? 'Open service provider', href: `/work/service-providers/${providerId}` });
  if (calendarEventId) links.push({ kind: 'Calendar', label: 'Open appointment', href: `/calendar/events/${calendarEventId}` });
  if (occurrence.communication_thread_id) links.push({ kind: 'Communication', label: 'Open provider conversation', href: `/communications/threads/${occurrence.communication_thread_id}` });
  for (const evidence of occurrence.evidence ?? []) {
    if (evidence.document_id) links.push({
      kind: 'Evidence',
      label: evidence.document_title ?? 'Open evidence',
      ...(evidence.review_status ? { status: evidence.review_status } : {}),
      href: `/documents/${evidence.document_id}`,
    });
  }
  return [...new Map(links.filter(link => link.href).map(link => [link.href, link])).values()];
}

export function ComplianceCycleTimeline({ occurrence }: { occurrence: ComplianceCycleOccurrence }) {
  const currentStage = occurrence.autopilot_stage ?? occurrence.stage ?? occurrence.status ?? 'Due';
  const activeIndex = stageIndex(currentStage);
  const dueDate = occurrence.occurrence_due_date ?? occurrence.scheduled_due_date;
  const attendance = occurrence.confirmed_attendance_at ?? occurrence.attendance_at ?? occurrence.proposed_attendance_at;
  const attention = (occurrence.attention ?? occurrence.attention_items ?? []).map(attentionText);
  const links = canonicalLinks(occurrence);
  const openFindings=(occurrence.findings??[]).filter(finding=>['Open','Action required','Awaiting verification'].includes(finding.status??''));
  const needsHuman = occurrence.needs_human === true || attention.length > 0 || openFindings.length>0;
  const intervention=occurrence.next_intervention&&!/no human intervention|no action required/i.test(occurrence.next_intervention)?occurrence.next_intervention:null;

  return <section className="panel compliance-cycle" aria-labelledby={`compliance-cycle-${occurrence.id}`}>
    <div className="section-heading compliance-cycle-heading">
      <div>
        <span className="eyebrow">{occurrence.occurrence_number ?? 'Current compliance cycle'}</span>
        <h2 id={`compliance-cycle-${occurrence.id}`}>Compliance cycle</h2>
        <p>One view of the requirement, work, appointment, evidence, findings and sign-off.</p>
      </div>
      <span className={`badge ${needsHuman ? 'warning' : ''}`}>{currentStage}</span>
    </div>

    <dl className="compliance-cycle-facts">
      <div><dt>Due</dt><dd>{formatDateOnly(dueDate, 'Unknown')}</dd></div>
      <div><dt>Provider</dt><dd>{occurrence.provider_name ?? 'Not yet appointed'}</dd></div>
      <div><dt>Attendance</dt><dd>{formatDateTime(attendance, 'Not yet confirmed')}</dd></div>
      <div><dt>Next due</dt><dd>{formatDateOnly(occurrence.next_due_date, occurrence.status === 'Completed' ? 'Being calculated' : 'Not yet available')}</dd></div>
    </dl>

    <ol className="compliance-cycle-timeline" aria-label="Compliance cycle progress" tabIndex={0}>
      {stages.map((stage, index) => {
        // A later workflow position cannot prove the missing approvals, delivery or evidence of earlier steps.
        const state = index === activeIndex ? 'current' : index < activeIndex ? 'earlier' : 'future';
        return <li key={stage.key} className={state} aria-current={state === 'current' ? 'step' : undefined}>
          <span aria-hidden="true">{index + 1}</span>
          <strong>{stage.label}</strong>
        </li>;
      })}
    </ol>
    <p className="workspace-source-note">Recorded workflow position. Check the linked work, approval, delivery and evidence records for completion.</p>

    {needsHuman ? <div className="compliance-cycle-attention" role="status">
      <h3>Needs attention</h3>
      {attention.length ? <ul>
        {attention.map((item, index) => <li key={`${item.title}-${index}`}>
          <div><strong>{item.title}</strong>{item.details && <span>{item.details}</span>}{item.severity && <small>{item.severity}</small>}</div>
          {item.href && <Link to={item.href}>Open record</Link>}
        </li>)}
      </ul> : <p>{intervention ?? (openFindings.length?`${openFindings.length} recorded findings need review.`:'A human decision is required before this cycle can continue.')}</p>}
    </div> : <div className="compliance-cycle-clear" role="status">
      <h3>No queued intervention</h3>
      <p>{intervention ?? 'The source returns no current queued intervention. Review evidence and approvals in their original records.'}</p>
    </div>}

    {(occurrence.findings?.length || occurrence.remedials?.length) ? <div className="compliance-cycle-records">
      {!!occurrence.findings?.length && <div><h3>Findings</h3>{occurrence.findings.map(item => <span key={item.id}><strong>{item.finding_number ?? 'Finding'} · {item.title}</strong><small>{item.status ?? 'Review required'}</small></span>)}</div>}
      {!!occurrence.remedials?.length && <div><h3>Remedials</h3>{occurrence.remedials.map(item => <span key={item.id}><strong>{item.action_number ?? item.work_number ?? 'Remedial'} · {item.title}</strong><small>{item.status ?? 'Open'}</small></span>)}</div>}
    </div> : null}

    {!!links.length && <nav className="compliance-cycle-links" aria-label="Compliance cycle records">
      {links.map(link => <Link key={link.href} to={link.href}><span>{link.kind ?? 'Record'}</span><strong>{link.label ?? link.reference ?? 'Open record'}</strong>{link.status && <small>{link.status}</small>}</Link>)}
    </nav>}
  </section>;
}
